export const getTextGradient = (theme: any): string => {
  const isDark = theme.palette.mode === "dark";
  // From PRD:
  // - Dark text gradient: linear-gradient(135deg, #fff 0%, #a5b4fc 100%)
  // - Light text gradient: linear-gradient(135deg, #1e293b 0%, #3b82f6 100%)
  return isDark
    ? "linear-gradient(135deg, #ffffff 0%, #a5b4fc 100%)"
    : "linear-gradient(135deg, #1e293b 0%, #3b82f6 100%)";
};

export const applyGradientText = (theme: any) => {
  const gradient = getTextGradient(theme);
  return {
    backgroundImage: gradient,
    backgroundClip: "text" as const,
    WebkitBackgroundClip: "text" as const,
    color: "transparent",
    WebkitTextFillColor: "transparent",
    display: "inline-block",
  };
};

type UnderlineOptions = {
  height?: number; // px
  inset?: string; // e.g., '10%', '0', '0.5rem'
};

export const applyGradientUnderline = (
  theme: any,
  options: UnderlineOptions = {}
) => {
  const { height = 3, inset = "10%" } = options;
  // Use same stops as text gradient for coherence; switch to 90deg for horizontal accent.
  const isDark = theme.palette.mode === "dark";
  const gradient = isDark
    ? "linear-gradient(90deg, #ffffff 0%, #a5b4fc 100%)"
    : "linear-gradient(90deg, #1e293b 0%, #3b82f6 100%)";

  return {
    position: "relative" as const,
    "&::after": {
      content: '""',
      position: "absolute" as const,
      left: inset,
      right: inset,
      bottom: 0,
      height,
      backgroundImage: gradient,
      borderRadius: height / 2,
      transform: "scaleX(0)",
      transformOrigin: "left center",
      transition: "transform 200ms ease",
    },
    "&:hover::after, &:focus-visible::after, &[data-active='true']::after": {
      transform: "scaleX(1)",
    },
  };
};

export const getAccentGradient = (theme: any, angle: string = "90deg"): string => {
  const isDark = theme.palette.mode === "dark";
  const stops = isDark
    ? "#ffffff 0%, #a5b4fc 100%"
    : "#1e293b 0%, #3b82f6 100%";
  return `linear-gradient(${angle}, ${stops})`;
};

// Variant gradients for different use cases
export type GradientVariant = "default" | "subtle" | "vibrant" | "soft" | "bold";

export const getGradientVariant = (
  theme: any,
  variant: GradientVariant = "default"
): string => {
  const isDark = theme.palette.mode === "dark";

  switch (variant) {
    case "subtle":
      // Softer, more muted gradient for small text and subtle accents
      return isDark
        ? "linear-gradient(135deg, #e2e8f0 0%, #94a3b8 100%)"
        : "linear-gradient(135deg, #475569 0%, #64748b 100%)";

    case "vibrant":
      // More saturated, eye-catching gradient
      return isDark
        ? "linear-gradient(135deg, #ffffff 0%, #818cf8 100%)"
        : "linear-gradient(135deg, #0f172a 0%, #2563eb 100%)";

    case "soft":
      // Very gentle gradient for backgrounds and large areas
      return isDark
        ? "linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(165,180,252,0.1) 100%)"
        : "linear-gradient(135deg, rgba(30,41,59,0.05) 0%, rgba(59,130,246,0.05) 100%)";

    case "bold":
      // High contrast gradient for emphasis
      return isDark
        ? "linear-gradient(135deg, #ffffff 0%, #6366f1 100%)"
        : "linear-gradient(135deg, #0f172a 0%, #1e40af 100%)";

    case "default":
    default:
      return getTextGradient(theme);
  }
};

// Apply gradient to small text (captions, labels, etc.)
export const applyGradientTextSmall = (theme: any, variant: GradientVariant = "subtle") => {
  const gradient = getGradientVariant(theme, variant);
  return {
    backgroundImage: gradient,
    backgroundClip: "text" as const,
    WebkitBackgroundClip: "text" as const,
    color: "transparent",
    WebkitTextFillColor: "transparent",
    display: "inline-block",
  };
};


// Background gradient variants
export const getBackgroundGradient = (
  theme: any,
  variant: "subtle" | "medium" | "strong" = "subtle"
): string => {
  const isDark = theme.palette.mode === "dark";

  switch (variant) {
    case "strong":
      return isDark
        ? "linear-gradient(135deg, rgba(255,255,255,0.15) 0%, rgba(165,180,252,0.15) 100%)"
        : "linear-gradient(135deg, rgba(30,41,59,0.1) 0%, rgba(59,130,246,0.1) 100%)";

    case "medium":
      return isDark
        ? "linear-gradient(135deg, rgba(255,255,255,0.08) 0%, rgba(165,180,252,0.08) 100%)"
        : "linear-gradient(135deg, rgba(30,41,59,0.06) 0%, rgba(59,130,246,0.06) 100%)";

    case "subtle":
    default:
      return isDark
        ? "linear-gradient(135deg, rgba(255,255,255,0.03) 0%, rgba(165,180,252,0.03) 100%)"
        : "linear-gradient(135deg, rgba(30,41,59,0.02) 0%, rgba(59,130,246,0.02) 100%)";
  }
};

// Border gradient
export const getBorderGradient = (theme: any, variant: GradientVariant = "default"): string => {
  const isDark = theme.palette.mode === "dark";
  const gradient = getGradientVariant(theme, variant);

  // For borders, we need to adjust opacity
  if (variant === "subtle") {
    return isDark
      ? "linear-gradient(135deg, rgba(255,255,255,0.2) 0%, rgba(165,180,252,0.2) 100%)"
      : "linear-gradient(135deg, rgba(30,41,59,0.15) 0%, rgba(59,130,246,0.15) 100%)";
  }

  return gradient;
};

// Apply gradient border
export const applyGradientBorder = (
  theme: any,
  width: number = 1,
  variant: GradientVariant = "default"
) => {
  const gradient = getBorderGradient(theme, variant);
  return {
    border: `${width}px solid transparent`,
    backgroundImage: `linear-gradient(${theme.palette.background.paper}, ${theme.palette.background.paper}), ${gradient}`,
    backgroundOrigin: "border-box" as const,
    backgroundClip: "padding-box, border-box" as const,
  };
};

